/**
 * Photographing a car the way a car-sharing app does: every side before the
 * keys change hands, the VIN and the mileage when it comes in, and every
 * existing mark on its own photo.
 *
 * Shared by the API, which refuses an incomplete inspection, and the portal,
 * which shows what is still missing — so the two can never disagree about
 * what "complete" means.
 */

/** The four sides, on record every time a car changes hands. */
export const SIDE_SLOTS = ['FRONT', 'REAR', 'LEFT', 'RIGHT'] as const;

/**
 * The car's papers, as it were: the VIN plate, and the dashboard with the
 * mileage and any warning lights. Demanded when a car is taken in, so there is
 * no arguing later about which car it was or how far it had been driven.
 */
export const RECORD_SLOTS = ['VIN', 'DASHBOARD'] as const;

/** Any number of these, each a single mark with what and where it is. */
export const DAMAGE_SLOT = 'DAMAGE' as const;

export const INSPECTION_SLOTS = [...SIDE_SLOTS, ...RECORD_SLOTS, 'INTERIOR', DAMAGE_SLOT] as const;
export type InspectionSlot = typeof INSPECTION_SLOTS[number];

export type InspectionKind = 'INTAKE' | 'HANDOVER';
type FixedSlot = Exclude<InspectionSlot, typeof DAMAGE_SLOT>;

/** What has to be photographed before the car is taken in or handed back, in the order it is asked for. */
export const REQUIRED_SLOTS: Record<InspectionKind, readonly FixedSlot[]> = {
  INTAKE: [...SIDE_SLOTS, ...RECORD_SLOTS],
  // The VIN does not change while the car is here; the mileage on the way out is worth having, not demanding.
  HANDOVER: SIDE_SLOTS,
};

/** Offered as well, never demanded. */
export const OPTIONAL_SLOTS: Record<InspectionKind, readonly FixedSlot[]> = {
  INTAKE: ['INTERIOR'],
  HANDOVER: ['DASHBOARD', 'INTERIOR'],
};

export const DAMAGE_TYPES = ['SCRATCH', 'CHIP', 'DENT', 'CRACK', 'RUST', 'OTHER'] as const;
export type DamageType = typeof DAMAGE_TYPES[number];

/** Where on the car a mark is. A short fixed list beats free text when searching history later. */
export const CAR_PARTS = [
  'BUMPER_FRONT', 'BUMPER_REAR', 'HOOD', 'ROOF', 'TRUNK',
  'FENDER_FRONT_LEFT', 'FENDER_FRONT_RIGHT', 'FENDER_REAR_LEFT', 'FENDER_REAR_RIGHT',
  'DOOR_FRONT_LEFT', 'DOOR_FRONT_RIGHT', 'DOOR_REAR_LEFT', 'DOOR_REAR_RIGHT',
  'SILL_LEFT', 'SILL_RIGHT', 'MIRROR_LEFT', 'MIRROR_RIGHT',
  'WINDSHIELD', 'REAR_WINDOW', 'SIDE_WINDOW', 'HEADLIGHT', 'TAILLIGHT',
  'WHEEL', 'INTERIOR', 'OTHER',
] as const;
export type CarPart = typeof CAR_PARTS[number];

/** The required photos not yet taken, in the order they are asked for. */
export function missingSlots(kind: InspectionKind, present: Iterable<string>): FixedSlot[] {
  const have = new Set(present);
  return REQUIRED_SLOTS[kind].filter((slot) => !have.has(slot));
}

/** The interior takes a series of shots — seats, boot, headlining; this many at most. */
export const MAX_INTERIOR_PHOTOS = 10;

/** Damage is marked one photo per spot; a walk-round never needs more than this. */
export const MAX_DAMAGE_PHOTOS = 40;

/** A side, the VIN or the dashboard holds one photo; a retake replaces it. Interior and damage pile up. */
export const isSinglePhotoSlot = (slot: InspectionSlot): boolean => slot !== DAMAGE_SLOT && slot !== 'INTERIOR';
